import type { PlayerProfile } from "./profile";
import catalog from "../theme/crestCatalog.json";

/**
 * Experience and player level.
 *
 * XP is earned by playing, not just by ticking off the catalog: every round you
 * finish pays out, winning pays more, a Perfect Match (an exact Five Out) drops
 * a little extra, and unlocking an achievement lands a proper chunk. Levelling
 * used to be a pure function of achievements unlocked, which meant a player
 * could grind a dozen rounds between tiers and see the bar sit still.
 */
export const XP_AWARDS: {
  /** Finishing a round, win or lose. Awarded on completion, not on the deal, so
   * dealing and immediately backing out can't farm levels. */
  readonly roundPlayed: number;
  /** On top of `roundPlayed`, for taking the round. Solo rounds never count as
   * wins (a lone player always empties their deck), matching the stats rules. */
  readonly roundWon: number;
  /** Per exact Match — the Five Out itself. Minor on its own, adds up over a hot round. */
  readonly perfectMatch: number;
  /** Per achievement unlocked, any tier. The biggest single payout there is —
   * worth about four won rounds. */
  readonly achievement: number;
  /** A campaign level's FIRST clear, any level. Flat rather than per level so
   * the game server can pay it from the count of levels cleared without
   * knowing the level list; a boss pays its extra through its chapter's
   * achievement instead. */
  readonly campaignLevel: number;
} = catalog.xpAwards;

/**
 * Entry N is the cumulative XP needed to REACH level N+1. Up to level 20 each
 * level costs 350 XP more than the one before it (700, 1050, 1400, ...), which
 * is a 73,150 XP climb to 20. From there every level costs what level 20 did,
 * 7,000, to the cap at 50.
 *
 * Calibrated against the first round a new player finishes. That round unlocks a
 * cluster of first-time achievements — six or seven of them, worth ~2,700 XP
 * between them — and a shallower curve let it skip a quarter of the ladder in
 * one go, handing over cosmetics gated at levels 5 and 7 before the player had
 * done anything to earn them. Even so, a first solo half-deck round still
 * reached level 4, so the achievement tiers themselves were raised: most
 * families no longer start at 1, and a first round now pays for two or three.
 *
 * The cost stops GROWING at 20 rather than the ladder stopping there because
 * the crest frames (theme/crests.ts) are spaced to slow down - 25 and 40 are
 * meant to be long hauls - and a cost still climbing by 350 a level would have
 * put level 40 past a quarter of a million XP. Flat, level 25 is ~108k and 40
 * is ~213k.
 *
 * The numbers live in theme/crestCatalog.json because the game server derives
 * a signed-in player's level from their cloud save to check the crest they
 * bring to an online table; `scripts/gen-crest-catalog.mjs` generates its copy.
 */
export const XP_THRESHOLDS = buildThresholds();

function buildThresholds(): number[] {
  const { maxLevel, firstCost, costStep, costStopsGrowingAt } = catalog.levels;
  const out = [0];
  for (let level = 2; level <= maxLevel; level++) {
    const cost = firstCost + costStep * (Math.min(level, costStopsGrowingAt) - 2);
    out.push(out[out.length - 1] + cost);
  }
  return out;
}

export const MAX_LEVEL = XP_THRESHOLDS.length;

export interface LevelProgress {
  level: number;
  /** Lifetime XP. */
  xp: number;
  /** XP needed to reach the current level. */
  currentLevelAt: number;
  /** XP needed for the next level, or null at max. */
  nextLevelAt: number | null;
  /** XP earned since reaching the current level. */
  intoLevel: number;
  /** XP still to earn for the next level, or null at max. */
  toNextLevel: number | null;
  /** 0-1 progress through the current level. 1 at max level. */
  fraction: number;
}

export function levelForXp(xp: number): number {
  let level = 1;
  for (let i = 0; i < XP_THRESHOLDS.length; i++) {
    if (xp >= XP_THRESHOLDS[i]) level = i + 1;
    else break;
  }
  return level;
}

export function levelProgress(profile: PlayerProfile): LevelProgress {
  return levelProgressForXp(profile.stats.xp);
}

/** Progress at an arbitrary XP total — used to show where a round's payout started from. */
export function levelProgressForXp(rawXp: number): LevelProgress {
  const xp = Math.max(0, rawXp);
  const level = levelForXp(xp);
  const currentLevelAt = XP_THRESHOLDS[level - 1];
  const nextLevelAt = level < MAX_LEVEL ? XP_THRESHOLDS[level] : null;
  const span = nextLevelAt === null ? 0 : nextLevelAt - currentLevelAt;
  const fraction = nextLevelAt === null || span <= 0 ? 1 : (xp - currentLevelAt) / span;
  return {
    level,
    xp,
    currentLevelAt,
    nextLevelAt,
    intoLevel: xp - currentLevelAt,
    toNextLevel: nextLevelAt === null ? null : nextLevelAt - xp,
    fraction: Math.max(0, Math.min(1, fraction)),
  };
}

/**
 * Where the bar starts and where it is going, for a payout of `earned` XP that
 * has already been banked into `xpNow`. Pure, so the arithmetic is tested.
 *
 * A level-up fills the OLD level's bar to the end first, then refills from
 * empty: running the bar backwards to the new level's position would read as
 * losing XP.
 */
export function xpSummaryPlan(xpNow: number, earned: number) {
  const after = levelProgressForXp(xpNow);
  const before = levelProgressForXp(xpNow - earned);
  const levelsGained = after.level - before.level;
  return {
    before,
    after,
    levelsGained,
    leveledUp: levelsGained > 0,
    /** The bar's width at the end of the first fill. */
    firstFillTo: levelsGained > 0 ? 1 : after.fraction,
  };
}
